import { useState, type FormEvent } from 'react'
import type {
  RecordingAutoUploadMode,
  RecordingAutoUploadState,
  UpdateRecordingAutoUploadRequest,
} from '@/types'
import styles from './nodeDetail.module.css'
import { cx } from './utils'

interface RecordingAutoUploadPanelProps {
  state: RecordingAutoUploadState | null
  configureDisabled: boolean
  runDisabled: boolean
  runningNow: boolean
  onConfigure: () => void
  onRunNow: () => void
}

export function RecordingAutoUploadPanel({
  state,
  configureDisabled,
  runDisabled,
  runningNow,
  onConfigure,
  onRunNow,
}: RecordingAutoUploadPanelProps) {
  const active = state?.mode === 'manual' || state?.mode === 'inference'
  const selected = state?.mode === 'manual'
    ? state.manual
    : state?.mode === 'inference'
      ? state.inference
      : null
  const supported = Boolean(state?.manual.supported || state?.inference.supported)
  const modeLabel = state?.mode === 'manual'
    ? 'Manual recordings'
    : state?.mode === 'inference'
      ? 'Inference recordings'
      : 'Off'
  const detail = active && selected
    ? `${modeLabel} · ${selected.local_time} ${selected.timezone} · Previous day · ${selected.delete_local_after_success ? 'Delete after upload' : 'Keep local files'}`
    : supported
      ? 'Manual upload only'
      : 'Agent update required'

  return (
    <div className={styles.autoUploadPanel}>
      <div className={styles.autoUploadPanelBody}>
        <div className={styles.autoUploadPanelTitleRow}>
          <span className={styles.cardTitle}>Recording Auto Upload</span>
          <span
            className={cx(
              styles.autoUploadStatusBadge,
              active && styles.autoUploadStatusOn,
              !active && styles.autoUploadStatusMuted,
            )}
          >
            {active ? `${modeLabel} ON` : 'Auto upload OFF'}
          </span>
        </div>
        <div className={styles.autoUploadPanelMeta}>{detail}</div>
      </div>
      <div className={styles.autoUploadPanelActions}>
        <button
          type="button"
          onClick={onRunNow}
          disabled={runDisabled || runningNow || !active || !selected?.supported}
          className={styles.secondaryActionButton}
        >
          {runningNow ? 'Running...' : 'Run previous day now'}
        </button>
        <button
          type="button"
          onClick={onConfigure}
          disabled={configureDisabled || !supported}
          className={styles.primaryActionButton}
        >
          Configure
        </button>
      </div>
    </div>
  )
}

interface RecordingAutoUploadModalProps {
  state: RecordingAutoUploadState
  canDeleteLocal: boolean
  onClose: () => void
  onSave: (body: UpdateRecordingAutoUploadRequest) => Promise<void>
}

export function RecordingAutoUploadModal({
  state,
  canDeleteLocal,
  onClose,
  onSave,
}: RecordingAutoUploadModalProps) {
  const initialMode = state.mode !== 'off'
    ? state.mode
    : state.manual.supported
      ? 'manual'
      : 'inference'
  const initialKind = state[initialMode]
  const [mode, setMode] = useState<RecordingAutoUploadMode>(state.mode)
  const [localTime, setLocalTime] = useState(initialKind.local_time)
  const [timezone, setTimezone] = useState(initialKind.timezone)
  const [deleteAfterUpload, setDeleteAfterUpload] = useState(initialKind.delete_local_after_success)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function selectMode(nextMode: RecordingAutoUploadMode) {
    setMode(nextMode)
    if (nextMode === 'off') return
    const next = state[nextMode]
    setLocalTime(next.local_time)
    setTimezone(next.timezone)
    setDeleteAfterUpload(next.delete_local_after_success)
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await onSave({
        mode,
        local_time: localTime,
        timezone,
        file_window: 'previous_day',
        delete_local_after_success: deleteAfterUpload,
      })
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? 'Failed to save auto upload setting.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.modalBackdrop} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <form className={styles.autoUploadModal} onSubmit={handleSubmit}>
        <div className={styles.autoUploadModalHeader}>
          <span className={styles.autoUploadModalTitle}>Configure Recording Auto Upload</span>
          <button type="button" onClick={onClose} className={styles.modalCloseButton} aria-label="Close">x</button>
        </div>
        {error && <div className={styles.autoUploadError}>{error}</div>}
        <div className={styles.autoUploadModeGroup} aria-label="Auto upload mode">
          {(['off', 'manual', 'inference'] as const).map((option) => {
            const optionSupported = option === 'off' || state[option].supported
            return (
              <button
                key={option}
                type="button"
                className={cx(
                  styles.autoUploadModeButton,
                  mode === option && styles.autoUploadModeButtonActive,
                )}
                disabled={!optionSupported}
                onClick={() => selectMode(option)}
              >
                {option === 'off' ? 'Off' : option === 'manual' ? 'Manual' : 'Inference'}
              </button>
            )
          })}
        </div>
        {mode !== 'off' && (
          <>
            <div className={styles.autoUploadFieldGrid}>
              <label className={styles.autoUploadField}>
                <span>Schedule time</span>
                <select value={localTime} onChange={(event) => setLocalTime(event.target.value)} className={styles.autoUploadInput}>
                  {Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`).map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label className={styles.autoUploadField}>
                <span>Timezone</span>
                <input value={timezone} onChange={(event) => setTimezone(event.target.value)} className={styles.autoUploadInput} />
              </label>
            </div>
            <div className={styles.autoUploadScopeBox}>
              <div className={styles.autoUploadScopeLabel}>Target</div>
              <div className={styles.autoUploadScopeValue}>Previous day {mode} recordings</div>
            </div>
            <label className={styles.autoUploadCheckboxLabel}>
              <input
                type="checkbox"
                checked={deleteAfterUpload}
                disabled={!canDeleteLocal}
                onChange={(event) => setDeleteAfterUpload(event.target.checked)}
              />
              <span>
                Delete local files after successful upload
                <span className={styles.dangerHint}>(irreversible)</span>
                {!canDeleteLocal && (
                  <span className={styles.dangerHint}>(permission required)</span>
                )}
              </span>
            </label>
          </>
        )}
        <div className={styles.autoUploadModalActions}>
          <button type="button" onClick={onClose} disabled={saving} className={styles.autoUploadModalCancel}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className={styles.autoUploadModalSubmit}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}
